// OTPForm.js
import React, { useEffect, useRef, useState } from "react";
import "./otpForm.css"; // optional, see next section
import { useNavigate } from "react-router-dom";


// =============================
// Helper functions
// =============================
const validateMobile = (mobile) => {
  if (!mobile) return "Mobile required";
  const v = mobile.trim();
  if (!/^\+?\d{10,15}$/.test(v))
    return "Enter in international format, digits only (with optional +).";
  return "";
};

const passwordScore = (pw) => {
  let score = 0;
  if (!pw) return 0;
  if (pw.length >= 8) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return Math.min(100, Math.round((score / 5) * 100));
};

const strengthLabel = (score) => {
  if (score === 0) return "—";
  if (score < 40) return "Weak";
  if (score < 70) return "Medium";
  return "Strong";
};

// =============================
// LocalStorage helpers
// =============================
const nowSeconds = () => Math.floor(Date.now() / 1000);
const recordSend = (purpose, mobile) => {
  const k = `otp_sends:${purpose}:${mobile}`;
  const obj = JSON.parse(localStorage.getItem(k) || "{}");
  const ts = nowSeconds();
  if (!obj.history) obj.history = [];
  obj.history.push(ts);
  obj.history = obj.history.filter((t) => t > ts - 24 * 3600);
  localStorage.setItem(k, JSON.stringify(obj));
};
const countSendsLastHour = (purpose, mobile) => {
  const k = `otp_sends:${purpose}:${mobile}`;
  const obj = JSON.parse(localStorage.getItem(k) || "{}");
  if (!obj.history) return 0;
  const ts = nowSeconds();
  return obj.history.filter((t) => t > ts - 3600).length;
};
const recordAttempt = (purpose, mobile) => {
  const k = `otp_attempts:${purpose}:${mobile}`;
  const obj = JSON.parse(localStorage.getItem(k) || "{}");
  const ts = nowSeconds();
  if (!obj.history) obj.history = [];
  obj.history.push(ts);
  obj.history = obj.history.filter((t) => t > ts - 3600);
  localStorage.setItem(k, JSON.stringify(obj));
};
const countAttemptsLastHour = (purpose, mobile) => {
  const k = `otp_attempts:${purpose}:${mobile}`;
  const obj = JSON.parse(localStorage.getItem(k) || "{}");
  if (!obj.history) return 0;
  const ts = nowSeconds();
  return obj.history.filter((t) => t > ts - 3600).length;
};

// =============================
// Mock API wrapper
// =============================
const API = {
  sendOtp: "https://dseapi.sadhanlab.com/api/register/",
  verifyOtp: "https://dseapi.sadhanlab.com/api/verify/",
  requestResetOtp: "https://dseapi.sadhanlab.com/api/request-reset-otp/",
  resetPassword: "https://dseapi.sadhanlab.com/api/reset-password/",
};

const apiPost = async (url, payload) => {
  const headers = { "Content-Type": "application/json" };
  const csrftoken = document.cookie.match(/(^|;)csrftoken=([^;]+)/)?.[2];
  if (csrftoken) headers["X-CSRFToken"] = csrftoken;

  const res = await fetch(url, {
    method: "POST",
    headers,
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });

  let json = {};
  try {
    json = await res.json();
  } catch {}
  return { status: res.status, body: json };
};

// =============================
// Main component
// =============================
const OTPForm = () => {
  // Core states
  const [regMobile, setRegMobile] = useState("");
  const [regOTP, setRegOTP] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regStrength, setRegStrength] = useState(0);
  const [regVerifyDisabled, setRegVerifyDisabled] = useState(false);

  const [resetMobile, setResetMobile] = useState("");
  const [resetOTP, setResetOTP] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetStrength, setResetStrength] = useState(0);
  const [resetVerifyDisabled, setResetVerifyDisabled] = useState(false);

  const [messages, setMessages] = useState([]);
  const [errors, setErrors] = useState({});
  const [showRegVerify, setShowRegVerify] = useState(false);
  const [showResetVerify, setShowResetVerify] = useState(false);

  // Timers
  const [regTimer, setRegTimer] = useState(120);
  const [resetTimer, setResetTimer] = useState(120);
  const regInterval = useRef(null);
  const resetInterval = useRef(null);

  const OTP_TTL = 120;

  // Navigate
  const navigate = useNavigate();


  // Captcha
  const regCaptchaRef = useRef(null);
  const resetCaptchaRef = useRef(null);
  const [regCaptchaId, setRegCaptchaId] = useState(null);
  const [resetCaptchaId, setResetCaptchaId] = useState(null);

  // Message display
  const showMessage = (text, type = "info") => {
    const id = Date.now();
    setMessages((msgs) => [...msgs, { id, text, type }]);
    setTimeout(() => setMessages((msgs) => msgs.filter((m) => m.id !== id)), 5000);
  };

  // Timers
  const startRegTimer = () => {
    clearInterval(regInterval.current);
    setRegTimer(OTP_TTL);
    regInterval.current = setInterval(() => {
      setRegTimer((t) => {
        if (t <= 1) {
          clearInterval(regInterval.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };
  const startResetTimer = () => {
    clearInterval(resetInterval.current);
    setResetTimer(OTP_TTL);
    resetInterval.current = setInterval(() => {
      setResetTimer((t) => {
        if (t <= 1) {
          clearInterval(resetInterval.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  // =========================
  // reCAPTCHA render (v2)
  // =========================
//   useEffect(() => {
//     const loadRecaptcha = () => {
//       if (!window.grecaptcha) {
//         const script = document.createElement("script");
//         script.src =
//           "https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit";
//         script.async = true;
//         script.defer = true;
//         document.body.appendChild(script);
//         window.onloadCallback = renderCaptchas;
//       } else {
//         renderCaptchas();
//       }
//     };

//     const renderCaptchas = () => {
//       if (regCaptchaRef.current && regCaptchaId === null) {
//         const id = window.grecaptcha.render(regCaptchaRef.current, {
//           sitekey: "6Lec9d0rAAAAAHpWeXmPNYDUhAP8EcCQ09WKEtoT", // ✅ replace with your real key
//         });
//         setRegCaptchaId(id);
//       }
//       if (resetCaptchaRef.current && resetCaptchaId === null) {
//         const id = window.grecaptcha.render(resetCaptchaRef.current, {
//           sitekey: "6Lec9d0rAAAAAHpWeXmPNYDUhAP8EcCQ09WKEtoT", // ✅ replace with your real key
//         });
//         setResetCaptchaId(id);
//       }
//     };

//     loadRecaptcha();
//   }, [regCaptchaId, resetCaptchaId]);



useEffect(() => {
  const renderCaptchas = () => {
    try {
      if (window.grecaptcha) {
        if (regCaptchaRef.current && regCaptchaId === null) {
          const existing = regCaptchaRef.current.querySelector("div");
          if (!existing) {
            const id = window.grecaptcha.render(regCaptchaRef.current, {
              sitekey: "6LeQj5ktAAAAABFAU2tCE6UOLfAh8I8I4MzSvtQK",
            });
            setRegCaptchaId(id);
          }
        }

        if (resetCaptchaRef.current && resetCaptchaId === null) {
          const existing = resetCaptchaRef.current.querySelector("div");
          if (!existing) {
            const id = window.grecaptcha.render(resetCaptchaRef.current, {
              sitekey: "6LeQj5ktAAAAABFAU2tCE6UOLfAh8I8I4MzSvtQK",
            });
            setResetCaptchaId(id);
          }
        }
      }
    } catch (e) {
      console.warn("reCAPTCHA render skipped:", e.message);
    }
  };

  const loadRecaptcha = () => {
    if (!window.grecaptcha) {
      const script = document.createElement("script");
      script.src =
        "https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      window.onloadCallback = renderCaptchas;
    } else {
      renderCaptchas();
    }
  };

  loadRecaptcha();
}, [regCaptchaId, resetCaptchaId]);




  // =========================
  // Register flow
  // =========================
  const sendRegOTP = async () => {
    setErrors({});
    const err = validateMobile(regMobile);
    if (err) return setErrors({ regMobile: err });
    if (countSendsLastHour("register", regMobile) >= 5)
      return setErrors({ regMobile: "Too many sends recently. Wait or contact support." });

    const captchaToken = window.grecaptcha.getResponse(regCaptchaId);
    if (!captchaToken) return setErrors({ regCaptcha: "Please complete CAPTCHA." });

    const { status, body } = await apiPost(API.sendOtp, {
      mobile: regMobile,
      purpose: "register",
      captcha_token: captchaToken,
    });
    if (status === 200 || status === 201) {


          // temporary solutions while OTP channel not working
      if (body.show_otp_to_user !== undefined && body.show_otp_to_user !== null) {
    alert(body.show_otp_to_user);
  }


      showMessage(body.message || "OTP sent.", "success");
      recordSend("register", regMobile);
      setShowRegVerify(true);
      startRegTimer();
    } else setErrors({ regMobile: body.error || "Failed to send OTP" });
  };

  const verifyRegOTP = async () => {
    setErrors({});
    if (!/^\d{4,6}$/.test(regOTP)) return setErrors({ regOTP: "Enter numeric OTP." });
    if (passwordScore(regPassword) < 40)
      return setErrors({ regOTP: "Choose a stronger password." });
    if (countAttemptsLastHour("register", regMobile) >= 5)
      return setErrors({ regOTP: "Too many attempts. Try later." });

    setRegVerifyDisabled(true);
    const { status, body } = await apiPost(API.verifyOtp, {
      mobile: regMobile,
      otp: regOTP,
      password: regPassword,
    });
    if (status === 200 || status === 201) {


        showMessage(body.message || "Registration successful.", "success");
        clearInterval(regInterval.current);
        setShowRegVerify(false);
        setRegMobile("");
        setRegOTP("");
        setRegPassword("");
        setTimeout(() => navigate("/"), 1000);

    //   showMessage(body.message || "Registration successful.", "success");
    //   clearInterval(regInterval.current);
    //   setShowRegVerify(false);
    //   setRegMobile("");
    //   setRegOTP("");
    //   setRegPassword("");
    } else {
      recordAttempt("register", regMobile);
      setErrors({ regOTP: body.error || "Invalid OTP." });
    }
    setRegVerifyDisabled(false);
  };

  // =========================
  // Reset flow
  // =========================
  const sendResetOTP = async () => {
    setErrors({});
    const err = validateMobile(resetMobile);
    if (err) return setErrors({ resetMobile: err });
    if (countSendsLastHour("reset", resetMobile) >= 5)
      return setErrors({ resetMobile: "Too many sends recently." });

    const captchaToken = window.grecaptcha.getResponse(resetCaptchaId);
    if (!captchaToken) return setErrors({ resetCaptcha: "Please complete CAPTCHA." });

    const { status, body } = await apiPost(API.requestResetOtp, {
      mobile: resetMobile,
      purpose: "reset",
      captcha_token: captchaToken,
    });
    if (status === 200 || status === 201) {


      // temporary solutions while OTP channel not working
      if (body.show_otp_to_user !== undefined && body.show_otp_to_user !== null) {
    alert(body.show_otp_to_user);
  }

      showMessage(body.message || "OTP sent.", "success");
      recordSend("reset", resetMobile);
      setShowResetVerify(true);
      startResetTimer();
    } else setErrors({ resetMobile: body.error || "Failed to send OTP" });
  };

  const verifyResetOTP = async () => {
    setErrors({});
    if (!/^\d{4,6}$/.test(resetOTP)) return setErrors({ resetOTP: "Enter numeric OTP." });
    if (passwordScore(resetPassword) < 40)
      return setErrors({ resetOTP: "Choose a stronger password." });
    if (countAttemptsLastHour("reset", resetMobile) >= 5)
      return setErrors({ resetOTP: "Too many attempts. Try later." });

    setResetVerifyDisabled(true);
    const { status, body } = await apiPost(API.resetPassword, {
      mobile: resetMobile,
      otp: resetOTP,
      new_password: resetPassword,
    });
    if (status === 200 || status === 201) {

    showMessage(body.message || "Password reset successful.", "success");
    clearInterval(resetInterval.current);
    setShowResetVerify(false);
    setResetMobile("");
    setResetOTP("");
    setResetPassword("");
    setTimeout(() => navigate("/"), 1000);


    //   showMessage(body.message || "Password reset successful.", "success");
    //   clearInterval(resetInterval.current);
    //   setShowResetVerify(false);
    //   setResetMobile("");
    //   setResetOTP("");
    //   setResetPassword("");
    } else {
      recordAttempt("reset", resetMobile);
      setErrors({ resetOTP: body.error || "Invalid OTP." });
    }
    setResetVerifyDisabled(false);
  };

  return (
    <div className="container py-4 otp-container">
      <h2 className="text-center text-primary mb-4">Registration and Reset Password</h2>

      {messages.map((m) => (
        <div
          key={m.id}
          className={`alert ${
            m.type === "success"
              ? "alert-success"
              : m.type === "danger"
              ? "alert-danger"
              : "alert-info"
          }`}
        >
          {m.text}
        </div>
      ))}

      <div className="row g-3">
        {/* Register */}
        <div className="col-12 col-md-6">
          <div className="card p-3 mb-3 shadow-sm">
            <h5 className="text-success" >Registration (via mobile)</h5>
            <input
              type="tel"
              className="form-control mb-2"
              placeholder="+8801XXXXXXXXX"
              value={regMobile}
              onChange={(e) => setRegMobile(e.target.value)}
            />
            {errors.regMobile && <div className="text-danger">{errors.regMobile}</div>}

            <div ref={regCaptchaRef} className="mb-2"></div>
            {errors.regCaptcha && <div className="text-danger">{errors.regCaptcha}</div>}

            <button className="btn btn-primary" onClick={sendRegOTP}>
              Send OTP
            </button>

            {showRegVerify && (
              <div className="mt-3">
                <p>Verify OTP sent to {regMobile}</p>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Enter OTP"
                  value={regOTP}
                  onChange={(e) => setRegOTP(e.target.value)}
                />
                {errors.regOTP && <div className="text-danger">{errors.regOTP}</div>}

                <input
                  type="password"
                  className="form-control mb-1"
                  placeholder="Enter password"
                  value={regPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    setRegPassword(val);
                    setRegStrength(passwordScore(val));
                  }}
                />

                <div className="password-strength">
                  <div
                    style={{
                      width: `${regStrength}%`,
                      background:
                        regStrength >= 70
                          ? "#2fbf71"
                          : regStrength >= 40
                          ? "#f0ad4e"
                          : "#e55353",
                    }}
                  />
                </div>

                <div className="small text-muted mb-2">
                  Strength: {strengthLabel(regStrength)}
                </div>

                <button
                  className="btn btn-success"
                  onClick={verifyRegOTP}
                  disabled={regVerifyDisabled}
                >
                  {regVerifyDisabled ? "Verifying..." : "Verify & Register"}
                </button>
                <div className="mt-2">Time left: {formatTime(regTimer)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Reset */}
        <div className="col-12 col-md-6">
          <div className="card p-3 mb-3 shadow-sm">
            <h5 className="text-success">Forgot password? Request OTP</h5>
            <input
              type="tel"
              className="form-control mb-2"
              placeholder="+8801XXXXXXXXX"
              value={resetMobile}
              onChange={(e) => setResetMobile(e.target.value)}
            />
            {errors.resetMobile && <div className="text-danger">{errors.resetMobile}</div>}

            <div ref={resetCaptchaRef} className="mb-2"></div>
            {errors.resetCaptcha && <div className="text-danger">{errors.resetCaptcha}</div>}

            <button className="btn btn-primary" onClick={sendResetOTP}>
              Send OTP
            </button>

            {showResetVerify && (
              <div className="mt-3">
                <p>Verify OTP sent to {resetMobile}</p>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Enter OTP"
                  value={resetOTP}
                  onChange={(e) => setResetOTP(e.target.value)}
                />
                {errors.resetOTP && <div className="text-danger">{errors.resetOTP}</div>}

                <input
                  type="password"
                  className="form-control mb-1"
                  placeholder="Enter new password"
                  value={resetPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    setResetPassword(val);
                    setResetStrength(passwordScore(val));
                  }}
                />

                <div className="password-strength">
                  <div
                    style={{
                      width: `${resetStrength}%`,
                      background:
                        resetStrength >= 70
                          ? "#2fbf71"
                          : resetStrength >= 40
                          ? "#f0ad4e"
                          : "#e55353",
                    }}
                  />
                </div>

                <div className="small text-muted mb-2">
                  Strength: {strengthLabel(resetStrength)}
                </div>

                <button
                  className="btn btn-success"
                  onClick={verifyResetOTP}
                  disabled={resetVerifyDisabled}
                >
                  {resetVerifyDisabled ? "Verifying..." : "Verify & Reset Password"}
                </button>
                <div className="mt-2">Time left: {formatTime(resetTimer)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPForm;
